import { t } from "@lingui/core/macro";
import { api } from "@/lib/api-client";
import { useCalendarStore } from "@/pages/Dashboard/Calendar/store/useCalendarStore";
import React, { useEffect } from "react";
import { FaUserAltSlash } from "react-icons/fa";
import { LuCopySlash, LuOctagonMinus, LuTimerOff } from "react-icons/lu";
import styled from "styled-components";
import { useAppointmentModalStore } from "../useAppointmentModalStore_";
import { AccordionItem, Spinner, Tooltip } from "@heroui/react";
import { LimeAccordion } from "@/Components/NextBase/LimeAccordion";
import { cn } from "@/utils";
import { Check } from "lucide-react";

const ServiceSelectList = ({
  serviceTags: tags,
  services,
  onSelectService,
  selectedServices,
}) => {
  const { lastSelectedUserId, lastSelectedResourceId, selectedLocationId } =
    useCalendarStore((state) => ({
      lastSelectedUserId: state.lastSelectedUserId,
      lastSelectedResourceId: state.lastSelectedResourceId,
      selectedLocationId: state.selectedLocationId,
    }));

  const serviceTags = tags.toSorted((a, b) => a.sortOrder - b.sortOrder);

  const { appointmentData, setRequiredTags } = useAppointmentModalStore(
    (s) => ({
      appointmentData: s.appointmentData,
      setRequiredTags: s.setRequiredTags,
    }),
  );

  const { data: nextWarnings, isPending: isNextWarningsPending } =
    api.calendar.useGetNextServiceWarnings({
      locationId: selectedLocationId,
      selectedServices: [
        ...structuredClone(selectedServices).map((ss) => ({
          ...ss,
        })),
      ],
      users: lastSelectedUserId ? [lastSelectedUserId] : [],
      resources: lastSelectedResourceId ? [lastSelectedResourceId] : [],
      startTime: appointmentData.startTime,
    });

  // REQUIRED SERVICES
  useEffect(() => {
    const nextRequiredTags = nextWarnings?.requiredTags || [];

    setRequiredTags((currentRequiredTags) => {
      if (
        currentRequiredTags.length === nextRequiredTags.length &&
        currentRequiredTags.every(
          (tagId, index) => tagId === nextRequiredTags[index],
        )
      ) {
        return currentRequiredTags;
      }

      return nextRequiredTags;
    });
  }, [nextWarnings?.requiredTags, setRequiredTags]);

  // if (serviceTags?.length === 0) {
  //   serviceTags.push({ name: t`Ostale storitve`, tagId: -1 });
  // }

  const servicesWithWarnings = services
    .filter(
      (s) =>
        !nextWarnings ||
        nextWarnings.availableServices.find(
          (ser) => s.serviceId === ser.serviceId && ser.isValid,
        ) ||
        selectedServices.find((ss) => ss.serviceId === s.serviceId),
    )
    .toSorted((a, b) => a.sortOrder - b.sortOrder)
    .map((service) => {
      const availableServiceDetails = nextWarnings?.availableServices.find(
        (s) => s.serviceId === service.serviceId,
      );

      return {
        ...service,
        id: service.serviceId,
        value: service.serviceId.toString(),
        label: service.name,
        ...availableServiceDetails,
        isValid: availableServiceDetails
          ? availableServiceDetails.isValid
          : true,
      };
    });

  const validServiceTags = serviceTags.filter((tag) => {
    const tagServices = servicesWithWarnings.filter(
      (service) =>
        (service.tagId === tag.tagId && service.permission) ||
        (tag.tagId === -1 && !service.tagId),
    );
    return tagServices.length > 0;
  });

  // Until the first warnings arrive we can't tell which services the selected user or resource performs
  if (isNextWarningsPending) {
    return (
      <div className="flex justify-center py-6">
        <Spinner size="sm" />
      </div>
    );
  }

  return (
    <div>
      <LimeAccordion
        isCompact
        defaultSelectedKeys={
          validServiceTags.length === 1
            ? [validServiceTags[0].tagId.toString()]
            : undefined
        }
        itemClasses={{
          trigger: "py-4",
        }}
      >
        {validServiceTags?.map((tag) => {
          const tagServices = servicesWithWarnings.filter(
            (service) =>
              (service.tagId === tag.tagId && service.permission) ||
              (tag.tagId === -1 && !service.tagId),
          );

          if (tagServices.length === 0) return null;

          const tagName = tag.name;

          return (
            <AccordionItem
              title={tagName}
              className="service-list-item"
              key={String(tag.tagId)}
              aria-label={tagName}
              data-identifier={`service-select-list-tag-${tagName}`}
            >
              <div className="flex flex-col gap-2">
                {tagServices.map((service) => {
                  const selectedItem = selectedServices.find(
                    (selectedService) =>
                      selectedService.serviceId === service.serviceId,
                  );

                  const isSelected = selectedItem != null;

                  return (
                    <ServiceItem
                      key={service.serviceId}
                      color={service.color}
                      isSelected={isSelected}
                      onClick={() => onSelectService(service, isSelected)}
                      type="button"
                    >
                      <div className={"color"}></div>
                      <div className="flex flex-1 items-center justify-between">
                        {service.name}

                        <div className="flex h-fit items-center gap-1">
                          {(
                            selectedItem
                              ? selectedItem.userRelationWarning
                              : service.userRelationWarning
                          ) ? (
                            <WarningIcon
                              label={t`Izbran zaposleni ne izvaja te storitve`}
                              icon={<FaUserAltSlash color="var(--error)" />}
                            />
                          ) : undefined}

                          {(
                            selectedItem
                              ? selectedItem.resourceRelationWarning
                              : service.resourceRelationWarning
                          ) ? (
                            <WarningIcon
                              label={t`Izbrano sredstvo ne izvaja te storitve`}
                              icon={<FaUserAltSlash color="var(--error)" />}
                            />
                          ) : undefined}

                          {(
                            selectedItem
                              ? selectedItem.userOverlapWarning
                              : service.userOverlapWarning
                          ) ? (
                            <WarningIcon
                              label={t`Zaposleni se prekriva`}
                              icon={<LuCopySlash color="var(--error)" />}
                            />
                          ) : undefined}

                          {(
                            selectedItem
                              ? selectedItem.resourceOverlapWarning
                              : service.resourceOverlapWarning
                          ) ? (
                            <WarningIcon
                              label={t`Sredstvo se prekriva`}
                              icon={<LuCopySlash color="var(--error)" />}
                            />
                          ) : undefined}

                          {(
                            selectedItem
                              ? selectedItem.userShiftWarning
                              : service.userShiftWarning
                          ) ? (
                            <WarningIcon
                              label={t`Zaposleni nima urnika`}
                              icon={<LuTimerOff color="var(--error)" />}
                            />
                          ) : undefined}

                          {(
                            selectedItem
                              ? selectedItem.resourceShiftWarning
                              : service.resourceShiftWarning
                          ) ? (
                            <WarningIcon
                              label={t`Sredstvo nima urnika`}
                              icon={<LuTimerOff color="var(--error)" />}
                            />
                          ) : undefined}

                          {(
                            selectedItem
                              ? selectedItem.restrictionWarning
                              : service.restrictionWarning
                          ) ? (
                            <WarningIcon
                              label={t`Omejitev storitve`}
                              icon={<LuOctagonMinus color="var(--error)" />}
                            />
                          ) : undefined}

                          {
                            <Check
                              size={16}
                              className={cn("ml-5", {
                                invisible: !isSelected,
                              })}
                              aria-hidden="true"
                            />
                          }
                        </div>
                      </div>
                    </ServiceItem>
                  );
                })}
              </div>
            </AccordionItem>
          );
        })}
      </LimeAccordion>
    </div>
  );
};

const WarningIcon = ({ icon, label }) => {
  return (
    <Tooltip
      content={label}
      classNames={{
        base: "pointer-events-none",
      }}
      closeDelay={0}
    >
      <div className="flex items-center">{icon}</div>
    </Tooltip>
  );
};

const ServiceItem = styled.button`
  display: flex;
  align-items: center;

  border-radius: 7px;
  padding: 0.5rem 1rem;

  border: 1px solid ${(props) => props.color};
  font-size: 0.92rem;

  &:hover {
    background-color: ${(props) => props.color}10;
  }

  .color {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background-color: ${(props) => props.color};
    margin-right: 0.5rem;
    flex-shrink: 0;
  }
`;

export default React.memo(ServiceSelectList);
